import { Component, Input } from '@angular/core';
import { HelpersService } from 'src/app/services';
import { JobcardService } from 'src/app/services/jobcard.service';
import { WorkshopJobcardQuoteService } from '../workshop-jobcard-quote/workshop-jobcard-quote.service';
import moment from 'moment';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { WorkshopJobcardAuditLogsComponent } from '../workshop-jobcard-live-updates/workshop-jobcard-audit-logs/workshop-jobcard-audit-logs.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-workshop-jobcard-jobcard-timer',
  templateUrl: './workshop-jobcard-jobcard-timer.component.html',
  styleUrls: ['./workshop-jobcard-jobcard-timer.component.scss']
})
export class WorkshopJobcardJobcardTimerComponent {

  @Input() jobcard: any;

  public existingDuration: number = 0;
  public allocatedJobcardDuration: number = 0;
  public elapsedDuration: string = '00:00:00';
  public activeTimer: any = null;
  private tickInterval: any = null;
  public showTimer: boolean = false;
  public timerStarting: boolean = false;
  public timers: any = [];  
  public jobcardTimerEnabled : boolean = false;

  constructor(
    private jobcardService: JobcardService,
    private helpersService: HelpersService,
    private workshopJobcardQuoteService: WorkshopJobcardQuoteService,
    public broadcastService: BroadcastService,
    private modalService: NgbModal,
  ) { }

  ngOnInit() {
    if (this.jobcard?.id) {
      this.elapsedDuration = '00:00:00';
      this.existingDuration = 0;
      this.activeTimer = null;
      this.showTimer = false;
      this.getJobCardTimer();
      this.jobcardTimerEnabled = this.helpersService.getPartnerData().EnableWorkshopTimer || false;
    }
  }

  ngOnChanges() {
    if (this.jobcard?.id) {
      this.getJobCardTimer();
    }
  }

  ngOnDestroy() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
    }
  }


  getJobCardTimer() {
    this.jobcardService.getServiceSpecificTimers(this.jobcard.partnerID, this.jobcard.id).subscribe((timers: any[]) => {
      this.timers = timers?.filter(x => x.endTime != null) || [];
      if (timers && timers.length >= 0) {
        this.activeTimer = timers.filter(t => t.endTime == null).pop();
        if (this.activeTimer) {
          this.jobcard.statusID = this.activeTimer.statusID;
        } else {
          this.jobcard.statusID = timers[0]?.statusID || this.jobcard.statusID;
        }
        this.allocatedJobcardDuration = timers[0]?.allocatedJobcardDuraton || 0;
        this.existingDuration = timers.reduce((acc, timer) => acc + (timer.durationSeconds || 0), 0);
        // Start ticking to update duration
        this.timerStarting = false;

        if (!this.tickInterval) {
          this.startTicking();
        }
        this.broadcastService.sendMessage('refresh-jobcard-timers');
      }
    });
  }

  startTicking() {
    this.tickInterval = setInterval(() => {
      this.updateAllDurations();
    }, 1000); // Update every second
  }

  updateAllDurations() {
    if (this.activeTimer && this.activeTimer.isPaused) return;

    if (this.activeTimer) {
      const duration = this.calculateDuration(this.activeTimer.startTime, this.existingDuration);
      this.elapsedDuration = duration;
    } else if (this.existingDuration > 0) {
      const duration = this.calculateDuration(moment.utc().format("YYYY-MM-DDTHH:mm:ss"), this.existingDuration);
      this.elapsedDuration = duration;
    } else {
      const duration = this.calculateDuration(moment.utc().format("YYYY-MM-DDTHH:mm:ss"), 0);
      this.elapsedDuration = duration;
    }
    this.showTimer = true;
  }

  // Calculate duration from UTC start time to now
  calculateDuration(utcStartTime: string, existingDuration: number): string {
    const startTime = new Date(utcStartTime + 'Z'); // Add 'Z' to indicate UTC
    const now = new Date();
    const diffMs = now.getTime() - startTime.getTime() + (existingDuration * 1000);

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return `${this.padZero(hours)}:${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  // Helper to pad single digits with zero
  padZero(num: number): string {
    return num.toString().padStart(2, '0');
  }

  isTimerOverdue(): boolean {
    const startTime = new Date((this.activeTimer ? this.activeTimer.startTime : moment.utc().format("YYYY-MM-DDTHH:mm:ss")) + 'Z'); // Add 'Z' to indicate UTC
    const now = new Date();
    const diffMs = now.getTime() - startTime.getTime() + (this.existingDuration * 1000);
    var isOverdue = diffMs >= ((this.allocatedJobcardDuration || (this.jobcard.serviceDuration * 60)) * 60 * 1000);
    return isOverdue;
  }

  onStartTimer() {
    this.timerStarting = true;
    this.jobcardService.startServiceTimer(this.jobcard.partnerID, this.jobcard.id).subscribe(() => {
      // Refresh timers after starting
      this.getJobCardTimer();
    }, () => {
      this.timerStarting = false;
    });
  }

  onStopTimer() {
    this.activeTimer.stopping = true;
    this.jobcardService.stopServiceTimer(this.activeTimer.partnerID, this.activeTimer.serviceID, this.activeTimer.id).subscribe(() => {
      // Refresh timers after stopping
      this.activeTimer.isPaused = true;
      this.getJobCardTimer();
    }, err => {
      this.activeTimer.stopping = false;
      this.activeTimer.isPaused = false;
    });
  }

  onPauseTimer() {
    this.activeTimer.pausing = true;
    this.jobcardService.pauseServiceTimer(this.activeTimer.partnerID, this.activeTimer.serviceID, this.activeTimer.id).subscribe(() => {
      this.activeTimer.isPaused = true;
      // Refresh timers after stopping
      this.getJobCardTimer();
    }, err => {
      this.activeTimer.isPaused = false;
      this.activeTimer.pausing = false;
    });
  }

  openJobCardLogs() {
    const modalRef = this.modalService.open(WorkshopJobcardAuditLogsComponent, {
      backdrop: true,
      backdropClass: 'jobcard-modal-popup-backdrop',
      windowClass: 'jobcard-modal-popup-window',
      size: 'lg'
    });

    modalRef.componentInstance.jobcard = this.jobcard;

    modalRef.result.then(
      (result) => {

      },
      (onrejected) => {

      },
    );
  }

}
